import React, { useMemo, memo, useCallback } from 'react';
import { includes, isEmpty } from 'lodash';

import PropTypes from 'prop-types';

import {
    getTemplateTextDisplayLabel,
    getTemplateTextValue,
} from './TemplateTextUtils';
import { Tooltips } from '../../../../constants/Tooltips';
import { TemplateTextDynamic } from './TemplateTextConsts';
import {
    AutotextDynamicTypes,
} from '../../../../constants/AutotextDynamicTypes';
import LinkTag from './dynamic-components/LinkTag';
import LinkWithTextTag from './dynamic-components/LinkWithTextTag';
import MissingValueTag from './dynamic-components/MissingValueTag';
import TextTag from './dynamic-components/TextTag';

const DynamicTag = (props) => {
    const {
        isDynamicDisabled,
        openTemplateTextChar,
        closeTemplateTextChar,
        templateTextContextData: templateContextData,
        onRemove,
        decoratedText,
        start,
        end,
        blockKey,
    } = props;

    const onRemoveTag = useCallback(() => {
        onRemove({ start, end, blockKey });
    }, [onRemove, start, end, blockKey]);

    const templateValue = useMemo(() => getTemplateTextValue(decoratedText), [decoratedText]);

    const dynamicValue = useMemo(() => {
        const realValue = templateContextData[templateValue];
        return !isEmpty(realValue) ? realValue : null;
    }, [templateValue, templateContextData]);

    const dynamicDisplayLabel = useMemo(() => dynamicValue?.text || getTemplateTextDisplayLabel(decoratedText), [decoratedText, dynamicValue]);
    const dynamicTemplateDisplayLabel = useMemo(() => `${openTemplateTextChar}${dynamicDisplayLabel}${closeTemplateTextChar}`, [dynamicDisplayLabel, templateContextData, openTemplateTextChar, closeTemplateTextChar]);

    const missingValueTooltip = useMemo(() => {
        switch (true) {
            case [
                TemplateTextDynamic.employerRepLinkedin,
                TemplateTextDynamic.employerRepPhone,
                TemplateTextDynamic.employerRepJobTitle,
                TemplateTextDynamic.employerLinkedin,
            ].includes(templateValue):
                return Tooltips[templateValue];
            default:
                return Tooltips.default;
        }
    }, [templateValue]);

    const { type, value } = dynamicValue || {};

    if (isDynamicDisabled && type !== AutotextDynamicTypes.text) {
        return dynamicDisplayLabel;
    }

    if (type === AutotextDynamicTypes.link) {
        if (value && !includes(value, dynamicDisplayLabel)) {
            return <LinkTag url={value} label={dynamicDisplayLabel} />;
        }
    }

    if (type === AutotextDynamicTypes.linkDynamicLabel) {
        if (templateValue === TemplateTextDynamic.employerCompanyLink) {
            const companyName = templateContextData?.employerName || 'Company (Hyperlink)';
            return <LinkWithTextTag url={value} label={companyName} onRemove={onRemoveTag}/>;
        }
    }

    if (type === AutotextDynamicTypes.text) {
        if (value && !includes(value, dynamicDisplayLabel)) {
            return <TextTag text={value} onRemove={onRemoveTag} />;
        }
    }

    return <MissingValueTag tooltip={missingValueTooltip} text={value || dynamicTemplateDisplayLabel} onRemove={onRemoveTag} />;
};

DynamicTag.defaulProps = {
    isDynamicDisabled: false,
    templateTextContextData: {},
    onRemove: () => {},
};

DynamicTag.propTypes = {
    isDynamicDisabled: PropTypes.bool,
    openTemplateTextChar: PropTypes.string,
    closeTemplateTextChar: PropTypes.string,
    templateTextOptions: PropTypes.array,
    templateTextContextData: PropTypes.object,
    onRemove: PropTypes.func,
    decoratedText: PropTypes.string,
    start: PropTypes.number,
    end: PropTypes.number,
    blockKey: PropTypes.string,
};

export default memo(DynamicTag);
